import Filters from '../../../components/blocks/filters/meTicketFilters';
import TicketsNoData from '../../../components/blocks/info/TicketsNoData';
import SellsStat from '../../../components/blocks/ticketStat/SellStat';
import SalesChart from '../../../components/blocks/ticketCharts/SalesChart';
import ViewsStat from '../../../components/blocks/ticketStat/ViewsStat';
import ViewsChart from '../../../components/blocks/ticketCharts/ViewsChart';
import TicketTypeSales from '../../../components/blocks/ticketCharts/TicketTypeSales';
import ReturnsStat from '../../../components/blocks/ticketStat/ReturnsStat';
import AvgPriceStat from '../../../components/blocks/ticketStat/AvgPriceStat';
import { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import useRequestLocation from '../../../hooks/useRequestLocation';
import useUrlParams from '../../../hooks/useUrlParams';
import EmptyContainer from '../EmptyContainer';
import FreePlanPlaceholder from '../FreePlanPlaceholder';
import { setPlan } from '../../../redux/slices/meTicketData';
import { setFilters } from '../../../redux/slices/filters';
import moment from 'moment';

export default function StatisticsContainer() {
  const [fullData, setFullData] = useState(undefined);
  const [availableEvents, setAvailableEvents] = useState({});
  const [availableEventMeta, setAvailableEventMeta] = useState({});
  const [previousData, setPreviousData] = useState(null);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const plan = useSelector((state) => state.meTicketData.plan);
  const filters = useSelector((state) => state.filters);
  const dispatch = useDispatch();
  const { getEventsFilters } = useRequestLocation();
  const { eventId } = useUrlParams();
  const isWaitingForInitialEvent = useRef(!!eventId);

  useEffect(() => {
    dispatch(
      setFilters({
        periodStart: null,
        periodEnd: null,
        eventsArray: [],
        isFitersActive: false
      })
    );
    setFiltersInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (!filtersInitialized) return;

    let ignore = false;
    setPreviousData(null);
    setIsLoadingStats(true);

    getEventsFilters(
      filters.periodStart,
      filters.periodEnd,
      filters.eventsArray
    ).then(async (data) => {
      if (ignore) return;

      dispatch(setPlan(data?.plan ?? null));

      if (
        data &&
        !filters.periodStart &&
        !filters.periodEnd &&
        filters.eventsArray.length === 0
      ) {
        setAvailableEvents(data.events ?? {});
        setAvailableEventMeta(data.eventMeta ?? {});

        if (isWaitingForInitialEvent.current) {
          isWaitingForInitialEvent.current = false;
          if (data.events?.[eventId]) {
            dispatch(
              setFilters({
                periodStart: null,
                periodEnd: null,
                eventsArray: [eventId],
                isFitersActive: true
              })
            );
            return;
          }
        }
      }

      setFullData(data);
      setIsLoadingStats(false);

      if (data && filters.periodStart && filters.periodEnd) {
        const start = moment(filters.periodStart);
        const end = moment(filters.periodEnd);
        const periodLength = end.diff(start, 'days') + 1;
        const previous = await getEventsFilters(
          start.clone().subtract(periodLength, 'days'),
          end.clone().subtract(periodLength, 'days'),
          filters.eventsArray
        );
        if (!ignore) setPreviousData(previous);
      }
    });

    return () => {
      ignore = true;
    };
  }, [dispatch, eventId, filters, filtersInitialized, getEventsFilters]);
 
  useEffect(() => {
    if (fullData && plan) {
      document.body.style.removeProperty('background-color');
    } else {
      document.body.style.setProperty('background-color', '#fff', 'important');
    }
    return () => document.body.style.removeProperty('background-color');
  }, [fullData, plan]);

  if (fullData === undefined) {
    return (
      <div className="d-flex justify-content-center align-items-center w-100 py-5 my-5">
        <Spinner animation="border" role="status" className="m-auto" />
      </div>
    );
  }

  if (fullData === null) return <EmptyContainer />;
  if (plan === null) return <FreePlanPlaceholder />;

  return (
    <>
      <Filters
        events={availableEvents}
        eventMeta={availableEventMeta}
        isLoadingStats={isLoadingStats}
      />
      {isLoadingStats ? (
        <div className="d-flex justify-content-center align-items-center w-100 py-5 my-5">
          <Spinner animation="border" role="status" className="m-auto" />
        </div>
      ) : (
        <>
          <TicketsNoData data={fullData} />
          <div className="col-md-6">
            <div className="row">
              <div className="col-12">
                <SellsStat data={fullData} previousData={previousData} />
              </div>
              <div className="col-12">
                <SalesChart data={fullData} />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="row">
              <div className="col-12">
                <ViewsStat data={fullData} previousData={previousData} />
              </div>
              <div className="col-12">
                <ViewsChart data={fullData} />
              </div>
            </div>
          </div>
          <TicketTypeSales data={fullData} />
          <ReturnsStat data={fullData} previousData={previousData} />
          <AvgPriceStat data={fullData} />
        </>
      )}
    </>
  );
}
